/**
 * Agency Home Page
 * Main dashboard for agency users
 * Protected route - requires authentication
 * 
 * @author Varun Pandey
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Grid3x3,
  List,
  MoreVertical,
  Plus,
  Building2,
  Users,
  UserCircle,
  LogOut,
  UserPlus,
  Eye,
  Pencil,
  Trash2,
  MessageSquare,
  AlertTriangle,
  Bell,
  ChevronsRight,
  Menu
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { PhoneInput } from '../../components/ui/PhoneInput';
import { Select, SelectOption } from '../../components/ui/Select';
import { MultiSelect, MultiSelectOption } from '../../components/ui/MultiSelect';
import { Checkbox } from '../../components/ui/Checkbox';
import { Sheet } from '../../components/ui/Sheet';
import { DropdownMenu, DropdownMenuItem } from '../../components/ui/DropdownMenu';
import { useAuthStore } from '../../stores/authStore';
import { useArtistDetailsStore } from '../../stores/artistDetailsStore';
import { clearArtistSessionCaches } from '../../stores/clearArtistSessionCaches';
import { AuthService, getAuthErrorMessage } from '../../services/authService';
import { getAgencyDetail, getAgencySubUsers, addAgencyArtist, createSubUser, updateSubUser, deleteSubUser, deleteArtistFromAgency } from '../../services/agencyService';
import type { Category } from '../../api/types';
import { EVENT_TYPES } from '../../constants/eventTypes';
import type { AgencyDetailData, AgencySubUser } from '../../services/agencyService';
import { LoggingOutOverlay } from '../../components/ui/LoggingOutOverlay';
import { getAuthCookies } from '../../utils/cookies';

// Module-level promise cache so initial fetches run only once (avoids double request in React Strict Mode)
let agencyDataPromise: Promise<[AgencyDetailData | null, AgencySubUser[]]> | null = null;
let categoriesPromise: Promise<Category[]> | null = null;
import { Loader } from '../../components/ui/Loader';
import { cn } from '../../utils/cn';
import toast from 'react-hot-toast';
import { INDIAN_PHONE_ERROR_MESSAGE, isValidIndianPhoneNumber } from '../../utils/phone';
import { STORAGE_KEYS } from '../../storage/storageKeys';

interface Artist {
  id: string;
  name: string;
  avatar?: string;
}

interface AgencyUser {
  id: string;
  name: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  assignedArtists?: string[];
  permissions: {
    view: boolean;
    edit: boolean;
  };
}


export const AgencyHome: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isAddArtistOpen, setIsAddArtistOpen] = useState(false);
  const [isRemoveArtistOpen, setIsRemoveArtistOpen] = useState(false);
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);

  // Close mobile menu when any sheet opens
  useEffect(() => {
    if (isAddArtistOpen || isRemoveArtistOpen || isAddUserOpen || isEditUserOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [isAddArtistOpen, isRemoveArtistOpen, isAddUserOpen, isEditUserOpen]);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);
  const [selectedUser, setSelectedUser] = useState<AgencyUser | null>(null);
  const [hoveredAgencyUsers, setHoveredAgencyUsers] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);
  const agencyUsersRef = useRef<HTMLDivElement | null>(null);
  const deleteArtistJustClickedRef = useRef(false);
  const fetchArtistDetails = useArtistDetailsStore((s) => s.fetchArtistDetails);

  const [isSwitchingArtist, setIsSwitchingArtist] = useState(false);

  // Form state for Add Artist
  const [professionalName, setProfessionalName] = useState('');
  const [category, setCategory] = useState('');
  const [genres, setGenres] = useState<string[]>([]);
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Dynamic category & subcategory (genre) data
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<SelectOption[]>([]);
  const [genreOptions, setGenreOptions] = useState<MultiSelectOption[]>([]);
  const [, setIsLoadingCategories] = useState(false);
  const [isLoadingGenres, setIsLoadingGenres] = useState(false);
  const [isSubmittingArtist, setIsSubmittingArtist] = useState(false);

  // Determine if currently selected category is an Influencer category
  const isInfluencerCategorySelected = useMemo(() => {
    if (!category) return false;
    const selected = categories.find((c) => c.slug === category);
    if (!selected) return false;
    const slug = (selected.slug || '').toLowerCase();
    const name = (selected.name || '').toLowerCase();
    return slug.includes('influencer') || name.includes('influencer');
  }, [category, categories]);

  // Form state for Remove Artist (single form object; fields shown depend on "Profile Managed By")
  const [deleteArtistForm, setDeleteArtistForm] = useState({
    reason: '',
    managedBy: '',
    agency_name: '',
    full_name: '',
    email: '',
    contact_no: '',
    relation_to_artist: '',
  });
  const [removeErrors, setRemoveErrors] = useState<Record<string, string>>({});

  // Form state for Add User
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [assignedArtists, setAssignedArtists] = useState<string[]>([]);
  const [canView, setCanView] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [userErrors, setUserErrors] = useState<Record<string, string>>({});
  const [isSubmittingUser, setIsSubmittingUser] = useState(false);

  // Form state for Edit User (all prefilled when opening except password)
  const [editFullName, setEditFullName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhoneNumber, setEditPhoneNumber] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editAssignedArtists, setEditAssignedArtists] = useState<string[]>([]);
  const [editCanView, setEditCanView] = useState(true);
  const [editCanEdit, setEditCanEdit] = useState(false);
  const [editUserErrors, setEditUserErrors] = useState<Record<string, string>>({});
  const [isSubmittingEditUser, setIsSubmittingEditUser] = useState(false);
  const [isDeletingUserId, setIsDeletingUserId] = useState<string | null>(null);

  // Agency data from API (api/v2/agency/detail/ and api/v2/agency/sub-users/)
  const [agencyDetail, setAgencyDetail] = useState<AgencyDetailData | null>(null);
  const [agencySubUsers, setAgencySubUsers] = useState<AgencySubUser[]>([]);
  const [loadingAgency, setLoadingAgency] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadingAgency(true);

    if (!agencyDataPromise) {
      agencyDataPromise = Promise.allSettled([
        getAgencyDetail(),
        getAgencySubUsers(),
      ]).then((results) => {
        const detailResult = results[0];
        const subUsersResult = results[1];

        const detail =
          detailResult.status === 'fulfilled' ? detailResult.value : null;
        const subUsers =
          subUsersResult.status === 'fulfilled' ? subUsersResult.value : [];

        return [detail, subUsers] as [AgencyDetailData | null, AgencySubUser[]];
      }).finally(() => {
        agencyDataPromise = null;
      });
    }

    agencyDataPromise
      .then(([detail, subUsers]) => {
        if (cancelled) return;
        if (detail) setAgencyDetail(detail);
        setAgencySubUsers(subUsers ?? []);
      })
      .catch((_e) => {
        if (!cancelled) {
          toast.error('We couldn’t load your agency details. Please refresh the page and try again.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingAgency(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const agencyName = agencyDetail?.agency_name ?? user?.name ?? '—';

  // Determine if current session is a sub user (from agency detail or auth cookies)
  const cookies = getAuthCookies();
  const normalizedAgencyRole = agencyDetail?.role
    ? agencyDetail.role.toLowerCase().replace(/[\s-]+/g, '_')
    : undefined;
  const normalizedCookieRole = cookies.role
    ? cookies.role.toLowerCase().replace(/[\s-]+/g, '_')
    : undefined;
  const isSubUser = normalizedAgencyRole === 'sub_user' || normalizedCookieRole === 'sub_user';
  const artists: Artist[] = (agencyDetail?.artists ?? []).map((a) => ({
    id: a.slug,
    name: a.professional_name,
    avatar: a.profile_pic ?? undefined,
  }));
  const agencyUsers: AgencyUser[] = (agencySubUsers ?? []).map((u) => ({
    id: String(u.id),
    name: u.full_name?.trim() || [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || '—',
    fullName: u.full_name?.trim(),
    firstName: u.first_name,
    lastName: u.last_name,
    email: u.email,
    phoneNumber: u.phone,
    assignedArtists: u.artist_slugs ?? [],
    permissions: { view: u.can_view, edit: u.can_edit },
  }));

  // Event type options (IDs from EVENT_TYPES constant)
  const eventTypeOptions: MultiSelectOption[] = EVENT_TYPES.map((t) => ({
    value: String(t.id),
    label: t.event_name,
  }));

  // Load categories once (used in Add Artist sheet); reuse in-flight promise to avoid double fetch in Strict Mode
  useEffect(() => {
    let cancelled = false;
    setIsLoadingCategories(true);
    if (!categoriesPromise) {
      categoriesPromise = AuthService.getCategories().finally(() => {
        categoriesPromise = null;
      });
    }
    categoriesPromise
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? (data as Category[]) : [];
        setCategories(list);
        setCategoryOptions(
          list.map((c) => ({
            value: c.slug,
            label: c.name,
          }))
        );
      })
      .catch((_e) => {
        if (!cancelled) {
          toast.error('We couldn’t load categories. Please try again.');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingCategories(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    sessionStorage.setItem(STORAGE_KEYS.SUPPRESS_LOGIN_REDIRECT_TOAST, '1');
    try {
      const success = await AuthService.logout();
      if (success) {
        sessionStorage.setItem(STORAGE_KEYS.LOGOUT_SUCCESS_MESSAGE, 'Logged out successfully.');
        navigate('/login', { replace: true });
      } else {
        sessionStorage.removeItem(STORAGE_KEYS.SUPPRESS_LOGIN_REDIRECT_TOAST);
        toast.error('Logout failed. Please try again.');
      }
    } catch (error) {
      sessionStorage.removeItem(STORAGE_KEYS.SUPPRESS_LOGIN_REDIRECT_TOAST);
      throw error;
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleAddArtist = () => {
    setIsAddArtistOpen(true);
  };

  const handleCloseSheet = () => {
    setIsAddArtistOpen(false);
    // Reset form
    setProfessionalName('');
    setCategory('');
    setGenres([]);
    setEventTypes([]);
    setErrors({});
    setGenreOptions([]);
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!professionalName.trim()) {
      newErrors.professionalName = 'Professional name is required';
    }
    if (!category) {
      newErrors.category = 'Category is required';
    }
    if (genres.length === 0) {
      newErrors.genres = 'At least one genre is required';
    }
    if (eventTypes.length === 0) {
      newErrors.eventTypes = 'At least one event type is required';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const first = Object.values(newErrors)[0];
      if (first) toast.error(first);
      return false;
    }
    return true;
  };

  const handleSubmitArtist = async () => {
    if (!validateForm() || isSubmittingArtist) {
      return;
    }

    // Map selected subcategory and event type values (strings) to numeric IDs for payload
    const subcategoryIds = genres
      .map((v) => parseInt(v, 10))
      .filter((n) => !Number.isNaN(n));
    const eventTypeIds = eventTypes
      .map((v) => parseInt(v, 10))
      .filter((n) => !Number.isNaN(n));

    if (subcategoryIds.length === 0 || eventTypeIds.length === 0) {
      // Safety guard; UI validation should already cover this
      toast.error('Please select at least one genre and one event type.');
      return;
    }

    const payload = {
      user: {
        professional_name: professionalName.trim(),
      },
      // Category in payload should be the category slug (e.g. "book-singer-online")
      category,
      subcategories: subcategoryIds,
      eventTypes: eventTypeIds,
    };

    setIsSubmittingArtist(true);
    try {
      await addAgencyArtist(payload);
      // Refresh agency detail so new artist appears in list
      const detail = await getAgencyDetail();
      if (detail) {
        setAgencyDetail(detail);
      }
      toast.success('Artist added to your agency.');
      handleCloseSheet();
    } catch (e) {
      toast.error('We couldn’t add the artist. Please try again.');
    } finally {
      setIsSubmittingArtist(false);
    }
  };

  const handleAddUser = () => {
    setIsAddUserOpen(true);
  };

  const handleCloseAddUserSheet = () => {
    setIsAddUserOpen(false);
    setFullName('');
    setPhoneNumber('');
    setEmail('');
    setPassword('');
    setAssignedArtists([]);
    setCanView(true);
    setCanEdit(false);
    setUserErrors({});
  };

  const isPasswordValid = (pwd: string) => {
    return /[A-Z]/.test(pwd) && /[0-9]/.test(pwd) && /[^A-Za-z0-9]/.test(pwd);
  };

  const validateUserForm = () => {
    const newErrors: Record<string, string> = {};
    if (!fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    if (!phoneNumber) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!isValidIndianPhoneNumber(phoneNumber)) {
      newErrors.phoneNumber = INDIAN_PHONE_ERROR_MESSAGE;
    }
    if (!email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email address is invalid';
    }
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (!isPasswordValid(password)) {
      newErrors.password = 'Password must include one uppercase letter, one number, and one special character';
    }
    if (assignedArtists.length === 0) {
      newErrors.assignedArtists = 'At least one artist must be assigned';
    }
    setUserErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const first = Object.values(newErrors)[0];
      if (first) toast.error(first);
      return false;
    }
    return true;
  };

  const getSubUserDuplicateToastMessage = (
    error: unknown,
    fallback: string,
  ): string => {
    const data = (error as any)?.response?.data;
    const nestedError = data?.error;
    const details = nestedError?.details;
    const messageArr = nestedError?.message;

    const firstString = (value: unknown): string | undefined => {
      if (typeof value === 'string') return value;
      if (Array.isArray(value)) {
        const found = value.find((v) => typeof v === 'string');
        return typeof found === 'string' ? found : undefined;
      }
      return undefined;
    };

    const messageObj = Array.isArray(messageArr) ? messageArr.find((m) => m && typeof m === 'object') : undefined;
    const emailFromMessage =
      messageObj && typeof messageObj === 'object' ? (messageObj as any).email : undefined;
    const phoneFromMessage =
      messageObj && typeof messageObj === 'object' ? (messageObj as any).phone : undefined;

    const emailDetail = firstString(details?.email) ?? firstString(emailFromMessage);
    const phoneDetail = firstString(details?.phone) ?? firstString(phoneFromMessage);

    const isAlreadyTaken = (s: string) => {
      const lower = s.toLowerCase();
      return lower.includes('already') && lower.includes('taken');
    };

    if (emailDetail && isAlreadyTaken(emailDetail)) {
      return emailDetail;
    }
    if (phoneDetail && isAlreadyTaken(phoneDetail)) {
      return phoneDetail;
    }

    // Generic fallback for any other invalid field shapes.
    return getAuthErrorMessage(error, fallback);
  };

  const handleSubmitUser = async () => {
    if (!validateUserForm() || isSubmittingUser) return;
    setIsSubmittingUser(true);
    try {
      await createSubUser({
        email: email.trim(),
        phone: phoneNumber,
        country_code: '+91',
        full_name: fullName.trim(),
        password,
        can_edit: canEdit,
        can_view: true,
        artist_slugs: assignedArtists,
      });
      toast.success('User added successfully.');
      const subUsers = await getAgencySubUsers();
      setAgencySubUsers(subUsers ?? []);
      handleCloseAddUserSheet();
    } catch (e: unknown) {
      toast.error(
        getSubUserDuplicateToastMessage(e, 'Failed to add user. Please try again.'),
      );
    } finally {
      setIsSubmittingUser(false);
    }
  };

  // Artist options for assignment (convert artists to MultiSelectOption format)
  const artistOptions: MultiSelectOption[] = artists.map((artist) => ({
    value: artist.id,
    label: artist.name,
  }));

  const handleModifyUser = (userId: string) => {
    const userToEdit = agencyUsers.find((u) => u.id === userId);
    if (userToEdit) {
      setSelectedUser(userToEdit);
      // Prefill all fields from user data; password stays empty (API provides full_name)
      const fullName = userToEdit.fullName || [userToEdit.firstName, userToEdit.lastName].filter(Boolean).join(' ').trim() || userToEdit.name || '';
      setEditFullName(fullName);
      setEditEmail(userToEdit.email || '');
      setEditPhoneNumber(userToEdit.phoneNumber || '');
      setEditPassword('');
      setEditAssignedArtists(userToEdit.assignedArtists || []);
      setEditCanView(userToEdit.permissions.view);
      setEditCanEdit(userToEdit.permissions.edit);
      setEditUserErrors({});
      setIsEditUserOpen(true);
    }
  };

  const handleCloseEditUserSheet = () => {
    setIsEditUserOpen(false);
    setSelectedUser(null);
    setEditFullName('');
    setEditEmail('');
    setEditPhoneNumber('');
    setEditPassword('');
    setEditAssignedArtists([]);
    setEditCanView(true);
    setEditCanEdit(false);
    setEditUserErrors({});
  };

  const validateEditUserForm = () => {
    const newErrors: Record<string, string> = {};
    if (!editFullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    if (!editEmail.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(editEmail)) {
      newErrors.email = 'Email address is invalid';
    }
    if (!editPhoneNumber) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!isValidIndianPhoneNumber(editPhoneNumber)) {
      newErrors.phoneNumber = INDIAN_PHONE_ERROR_MESSAGE;
    }
    if (editPassword && !isPasswordValid(editPassword)) {
      newErrors.password = 'Password must include one uppercase letter, one number, and one special character';
    }
    setEditUserErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const first = Object.values(newErrors)[0];
      if (first) toast.error(first);
      return false;
    }
    return true;
  };

  const handleSubmitEditUser = async () => {
    if (!validateEditUserForm() || !selectedUser || isSubmittingEditUser) return;
    setIsSubmittingEditUser(true);
    const payload = {
      email: editEmail.trim(),
      phone: editPhoneNumber,
      country_code: '+91',
      full_name: editFullName.trim(),
      can_edit: editCanEdit,
      can_view: editCanView,
      artist_slugs: editAssignedArtists,
      ...(editPassword.trim() ? { password: editPassword } : {}),
    };
    try {
      await updateSubUser(Number(selectedUser.id), payload);
      toast.success('User updated successfully.');
      const subUsers = await getAgencySubUsers();
      setAgencySubUsers(subUsers ?? []);
      handleCloseEditUserSheet();
    } catch (e: unknown) {
      toast.error(
        getSubUserDuplicateToastMessage(e, 'Failed to update user. Please try again.'),
      );
    } finally {
      setIsSubmittingEditUser(false);
    }
  };

  const handleRemoveArtist = (artist: Artist) => {
    setSelectedArtist(artist);
    setIsRemoveArtistOpen(true);
  };

  const handleCloseRemoveSheet = () => {
    setIsRemoveArtistOpen(false);
    setSelectedArtist(null);
    setDeleteArtistForm({
      reason: '',
      managedBy: '',
      agency_name: '',
      full_name: '',
      email: '',
      contact_no: '',
      relation_to_artist: '',
    });
    setRemoveErrors({});
  };

  // Real-time validation for delete-artist form fields
  const validateDeleteArtistField = useCallback((fieldName: string, value: string, managedBy?: string) => {
    setRemoveErrors((prev) => {
      const next = { ...prev };
      switch (fieldName) {
        case 'reason':
          if (!value.trim()) next.reason = 'Reason for removal is required';
          else if (value.length > 200) next.reason = 'Reason cannot exceed 200 characters';
          else delete next.reason;
          break;
        case 'agency_name':
          if (managedBy === 'agency' && !value.trim()) next.agency_name = 'Agency name is required';
          else if (value.length > 100) next.agency_name = 'Agency name cannot exceed 100 characters';
          else delete next.agency_name;
          break;
        case 'full_name':
          if (!value.trim()) next.full_name = 'Full name is required';
          else if (value.length > 100) next.full_name = 'Full name cannot exceed 100 characters';
          else delete next.full_name;
          break;
        case 'email':
          if (!value.trim()) next.email = 'Email is required';
          else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) next.email = 'Please enter a valid email address';
          else delete next.email;
          break;
        case 'contact_no':
          if (!value.trim()) next.contact_no = 'Phone number is required';
          else if (!isValidIndianPhoneNumber(value)) next.contact_no = INDIAN_PHONE_ERROR_MESSAGE;
          else delete next.contact_no;
          break;
        case 'relation_to_artist':
          if (managedBy === 'family' && !value.trim()) next.relation_to_artist = 'Relation to artist is required';
          else if (value.length > 50) next.relation_to_artist = 'Relation cannot exceed 50 characters';
          else delete next.relation_to_artist;
          break;
        default:
          break;
      }
      return next;
    });
  }, []);

  const validateDeleteArtistForm = useCallback((): boolean => {
    const { reason, managedBy, full_name, email, contact_no, agency_name, relation_to_artist } = deleteArtistForm;
    const err: Record<string, string> = {};
    if (!reason.trim()) err.reason = 'Reason for removal is required';
    else if (reason.length > 200) err.reason = 'Reason cannot exceed 200 characters';
    if (!managedBy) err.managedBy = 'Please select who will manage the artist';
    if (managedBy === 'agency' && !agency_name.trim()) err.agency_name = 'Agency name is required';
    if (managedBy === 'family' && !relation_to_artist.trim()) err.relation_to_artist = 'Relation to artist is required';
    if (!full_name.trim()) err.full_name = 'Full name is required';
    else if (full_name.length > 100) err.full_name = 'Full name cannot exceed 100 characters';
    if (!email.trim()) err.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) err.email = 'Please enter a valid email address';
    if (!contact_no.trim()) err.contact_no = 'Phone number is required';
    else if (!isValidIndianPhoneNumber(contact_no)) err.contact_no = INDIAN_PHONE_ERROR_MESSAGE;
    setRemoveErrors(err);
    if (Object.keys(err).length > 0) {
      const first = Object.values(err)[0];
      if (first) toast.error(first);
      return false;
    }
    return true;
  }, [deleteArtistForm]);

  /** Builds the delete-artist request payload. Shape matches API: full_name in all cases; + relation_to_artist for family; + agency_name for agency. */
  const getDeleteArtistPayload = useCallback((): import('../../services/agencyService').DeleteArtistFromAgencyPayload | null => {
    if (!selectedArtist || !deleteArtistForm.managedBy) return null;
    const managedBy = deleteArtistForm.managedBy as 'artist' | 'family' | 'manager' | 'agency';
    const base: import('../../services/agencyService').DeleteArtistFromAgencyPayload = {
      artist_slug: selectedArtist.id,
      reason: deleteArtistForm.reason,
      managed_by: managedBy,
      full_name: deleteArtistForm.full_name,
      email: deleteArtistForm.email,
      contact_no: deleteArtistForm.contact_no,
    };
    if (managedBy === 'family') base.relation_to_artist = deleteArtistForm.relation_to_artist;
    if (managedBy === 'agency') base.agency_name = deleteArtistForm.agency_name;
    return base;
  }, [selectedArtist, deleteArtistForm]);

  const [isDeletingArtist, setIsDeletingArtist] = useState(false);

  const handleSubmitRemoveArtist = async () => {
    if (!selectedArtist || !validateDeleteArtistForm()) return;
    const payload = getDeleteArtistPayload();
    if (!payload) return;
    setIsDeletingArtist(true);
    try {
      await deleteArtistFromAgency(payload);
      toast.success('Artist removed from your agency.');
      handleCloseRemoveSheet();
      const [detail, subUsers] = await Promise.all([getAgencyDetail(), getAgencySubUsers()]);
      if (detail) setAgencyDetail(detail);
      setAgencySubUsers(subUsers ?? []);
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'response' in e && typeof (e as { response?: { data?: { message?: string } } }).response?.data?.message === 'string'
        ? (e as { response: { data: { message: string } } }).response.data.message
        : 'Failed to remove artist. Please try again.';
      toast.error(msg);
    } finally {
      setIsDeletingArtist(false);
    }
  };

  // Profile managed by options for removal (same as old IsAgency – payload keys: agency, family, manager, artist)
  const profileManagedByOptions: SelectOption[] = [
    { value: 'agency', label: 'Agency' },
    { value: 'family', label: 'Family' },
    { value: 'manager', label: 'Manager' },
    { value: 'artist', label: 'Self (Artist)' },
  ];

  /** Renders extra fields in the Remove Artist sheet based on "Profile Managed By" selection. */
  const renderDeleteArtistDynamicFields = () => {
    const { managedBy } = deleteArtistForm;
    switch (managedBy) {
      case 'agency':
        return (
          <>
            <div className="w-full">
              <Input
                label="New Agency's Name *"
                placeholder="Enter the new agency's name"
                value={deleteArtistForm.agency_name}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 100);
                  setDeleteArtistForm((prev) => ({ ...prev, agency_name: value }));
                  validateDeleteArtistField('agency_name', value, managedBy);
                }}
                error={removeErrors.agency_name}
                hideErrorText
              />
              <p className="text-xs text-[#8E8E93] mt-1">{deleteArtistForm.agency_name.length}/100</p>
            </div>
            <div className="w-full">
              <Input
                label="Full Name of the New POC *"
                placeholder="Enter full name"
                value={deleteArtistForm.full_name}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 100);
                  setDeleteArtistForm((prev) => ({ ...prev, full_name: value }));
                  validateDeleteArtistField('full_name', value);
                }}
                error={removeErrors.full_name}
                hideErrorText
              />
              <p className="text-xs text-[#8E8E93] mt-1">{deleteArtistForm.full_name.length}/100</p>
            </div>
            <div className="w-full">
              <Input
                label="Email Address of the New POC *"
                type="email"
                placeholder="Enter email address"
                value={deleteArtistForm.email}
                onChange={(e) => {
                  setDeleteArtistForm((prev) => ({ ...prev, email: e.target.value }));
                  validateDeleteArtistField('email', e.target.value);
                }}
                error={removeErrors.email}
                hideErrorText
              />
            </div>
            <div className="w-full">
              <PhoneInput
                label="Phone Number of the New POC *"
                value={deleteArtistForm.contact_no}
                onChange={(v) => {
                  setDeleteArtistForm((prev) => ({ ...prev, contact_no: v }));
                  validateDeleteArtistField('contact_no', v);
                }}
                placeholder="Enter 10-digit contact number"
                error={removeErrors.contact_no}
                hideErrorText
              />
            </div>
          </>
        );
      case 'family':
        return (
          <>
            <div className="w-full">
              <Input
                label="Relation to the Artist *"
                placeholder="e.g., Father, Mother, Brother, Sister"
                value={deleteArtistForm.relation_to_artist}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 50);
                  setDeleteArtistForm((prev) => ({ ...prev, relation_to_artist: value }));
                  validateDeleteArtistField('relation_to_artist', value, managedBy);
                }}
                error={removeErrors.relation_to_artist}
                hideErrorText
              />
              <p className="text-xs text-[#8E8E93] mt-1">{deleteArtistForm.relation_to_artist.length}/50</p>
            </div>
            <div className="w-full">
              <Input
                label="Full Name of the New POC *"
                placeholder="Enter full name"
                value={deleteArtistForm.full_name}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 100);
                  setDeleteArtistForm((prev) => ({ ...prev, full_name: value }));
                  validateDeleteArtistField('full_name', value);
                }}
                error={removeErrors.full_name}
                hideErrorText
              />
              <p className="text-xs text-[#8E8E93] mt-1">{deleteArtistForm.full_name.length}/100</p>
            </div>
            <div className="w-full">
              <Input
                label="Email Address of the New POC *"
                type="email"
                placeholder="Enter email address"
                value={deleteArtistForm.email}
                onChange={(e) => {
                  setDeleteArtistForm((prev) => ({ ...prev, email: e.target.value }));
                  validateDeleteArtistField('email', e.target.value);
                }}
                error={removeErrors.email}
                hideErrorText
              />
            </div>
            <div className="w-full">
              <PhoneInput
                label="Phone Number of the New POC *"
                value={deleteArtistForm.contact_no}
                onChange={(v) => {
                  setDeleteArtistForm((prev) => ({ ...prev, contact_no: v }));
                  validateDeleteArtistField('contact_no', v);
                }}
                placeholder="Enter 10-digit contact number"
                error={removeErrors.contact_no}
                hideErrorText
              />
            </div>
          </>
        );
      case 'manager':
        return (
          <>
            <div className="w-full">
              <Input
                label="Manager's Full Name *"
                placeholder="Enter full name"
                value={deleteArtistForm.full_name}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 100);
                  setDeleteArtistForm((prev) => ({ ...prev, full_name: value }));
                  validateDeleteArtistField('full_name', value);
                }}
                error={removeErrors.full_name}
                hideErrorText
              />
              <p className="text-xs text-[#8E8E93] mt-1">{deleteArtistForm.full_name.length}/100</p>
            </div>
            <div className="w-full">
              <Input
                label="Manager's Email Address *"
                type="email"
                placeholder="Enter email address"
                value={deleteArtistForm.email}
                onChange={(e) => {
                  setDeleteArtistForm((prev) => ({ ...prev, email: e.target.value }));
                  validateDeleteArtistField('email', e.target.value);
                }}
                error={removeErrors.email}
                hideErrorText
              />
            </div>
            <div className="w-full">
              <PhoneInput
                label="Manager's Phone Number *"
                value={deleteArtistForm.contact_no}
                onChange={(v) => {
                  setDeleteArtistForm((prev) => ({ ...prev, contact_no: v }));
                  validateDeleteArtistField('contact_no', v);
                }}
                placeholder="Enter 10-digit contact number"
                error={removeErrors.contact_no}
                hideErrorText
              />
            </div>
          </>
        );
      case 'artist':
        return (
          <>
            <div className="w-full">
              <Input
                label="Full Name *"
                placeholder="Enter full name"
                value={deleteArtistForm.full_name}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 100);
                  setDeleteArtistForm((prev) => ({ ...prev, full_name: value }));
                  validateDeleteArtistField('full_name', value);
                }}
                error={removeErrors.full_name}
                hideErrorText
              />
              <p className="text-xs text-[#8E8E93] mt-1">{deleteArtistForm.full_name.length}/100</p>
            </div>
            <div className="w-full">
              <Input
                label="Email Address *"
                type="email"
                placeholder="Enter email address"
                value={deleteArtistForm.email}
                onChange={(e) => {
                  setDeleteArtistForm((prev) => ({ ...prev, email: e.target.value }));
                  validateDeleteArtistField('email', e.target.value);
                }}
                error={removeErrors.email}
                hideErrorText
              />
            </div>
            <div className="w-full">
              <PhoneInput
                label="Phone Number *"
                value={deleteArtistForm.contact_no}
                onChange={(v) => {
                  setDeleteArtistForm((prev) => ({ ...prev, contact_no: v }));
                  validateDeleteArtistField('contact_no', v);
                }}
                placeholder="Enter 10-digit contact number"
                error={removeErrors.contact_no}
                hideErrorText
              />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  // Update popover position when agency users is hovered
  useEffect(() => {
    if (hoveredAgencyUsers && agencyUsersRef.current) {
      const element = agencyUsersRef.current;
      if (element) {
        const rect = element.getBoundingClientRect();
        setPopoverPosition({
          top: rect.top,
          left: rect.right + 8, // 8px gap
        });
      }
    } else {
      setPopoverPosition(null);
    }
  }, [hoveredAgencyUsers]);

  const handleDeleteUser = async (userId: string): Promise<boolean> => {
    if (isDeletingUserId) return false;
    const u = agencyUsers.find((x) => x.id === userId);
    if (!u) return false;
    setIsDeletingUserId(userId);
    try {
      await deleteSubUser(Number(userId));
      toast.success(`User "${u.name}" removed.`);
      const subUsers = await getAgencySubUsers();
      setAgencySubUsers(subUsers ?? []);
      return true;
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'response' in e && typeof (e as { response?: { data?: { message?: string } } }).response?.data?.message === 'string'
        ? (e as { response: { data: { message: string } } }).response.data.message
        : 'Failed to remove user. Please try again.';
      toast.error(msg);
      return false;
    } finally {
      setIsDeletingUserId(null);
    }
  };

  return (
    <div className="h-screen bg-[#0B0B0D] text-white flex flex-col overflow-hidden pt-16">
      {isLoggingOut && <LoggingOutOverlay />}
      {(loadingAgency || isSwitchingArtist) && (
        <Loader
          size="fullscreen"
          message={
            isSwitchingArtist
              ? 'Fetching Details...'
              : 'Getting ready...'
          }
        />
      )}
      {/* Top Navbar - blurred & transparent, similar to UserHome */}
      <header
        className="
          fixed top-0 z-[70]
          h-16 w-full
          flex items-center justify-between px-4 lg:px-6
          bg-transparent
          backdrop-blur-md
          supports-[backdrop-filter]:bg-transparent
          after:absolute after:inset-x-0 after:bottom-0 after:h-6
          after:pointer-events-none
        "
        style={{
          WebkitMaskImage:
            'linear-gradient(to bottom, black 0%, black 70%, transparent 100%)',
          maskImage:
            'linear-gradient(to bottom, black 0%, black 70%, transparent 100%)',
        }}
      >
        {/* Left Side - Mobile Menu & Logo Placeholder */}
        <div className="flex items-center gap-2">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="lg:hidden p-2 rounded-lg hover:bg-[#2C2C2E] transition-colors"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5 text-white" />
          </button>
          {/* Logo placeholder (desktop) */}
          <div className="hidden lg:block flex-1" />
        </div>

        {/* Right Side Actions */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setIsNotificationsOpen(true)}
            className="p-2 rounded-lg hover:bg-[#2C2C2E] transition-colors relative"
            aria-label="Notifications"
          >
            <Bell className="h-5 w-5 text-white" />
            {/* TODO: Add notification badge if unread count > 0 */}
          </button>
        </div>
      </header>

      {/* Main Content Area - Sidebar + Content */}
      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* Left Sidebar - Hidden on Mobile */}
        <div className="hidden lg:block h-full">
          <aside className="flex w-72 bg-[#1C1C1E]/60 flex-col shadow-2xl h-full overflow-hidden">
            {/* Profile Section */}
            <div className="relative px-6 pt-20 pb-6">
              <div className="flex flex-col items-center gap-4">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-[#3A3A3C] flex items-center justify-center shadow-[0_0_40px_rgba(0,0,0,0.6)]">
                    <Building2 className="h-12 w-12 text-[#8E8E93]" />
                  </div>
                </div>
                <div className="text-center space-y-1">
                  <h2 className="text-xl font-black tracking-tight text-white">{agencyName}</h2>
                  <p className="text-xs font-medium uppercase tracking-[0.18em] text-[#8E8E93]">
                    Agency Console
                  </p>
                </div>
              </div>

              {/* Divider */}
              <div className="mt-8 flex justify-center">
                <div className="h-px w-full bg-gradient-to-r from-transparent via-[#3A3A3C] to-transparent" />
              </div>
            </div>

            {/* Agency Console Content */}
            <div className="flex-1 overflow-y-auto w-full px-4 pb-3 min-h-0">
              <div className="flex flex-col gap-3 w-full">
                {/* Agency Snapshot Card */}
                <div className="rounded-2xl bg-[#0B0B0D] border border-[#3A3A3C] px-3 py-3 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-xl bg-[#F33363]/10 flex items-center justify-center">
                        <Building2 className="h-4 w-4 text-[#F33363]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-[0.16em]">
                          Artists Managed
                        </p>
                        <p className="text-sm font-semibold text-white truncate">
                          {artists.length}
                        </p>
                      </div>
                    </div>
                  </div>

                </div>

                {/* Agency Users quick entry */}

              </div>
            </div>

            {/* Logout Button - match UserHome style */}
            <div className="p-4 mt-auto border-t border-[#3A3A3C]/50">
              <button
                onClick={handleLogout}
                className="w-full px-4 py-2.5 rounded-lg bg-[#2C2C2E] hover:bg-[#3A3A3C] text-white text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            </div>

            {/* Agency Users Hover Popover Portal - Desktop Only */}
            {hoveredAgencyUsers && popoverPosition && typeof document !== 'undefined' ? (
              createPortal(
                <div
                  className="hidden lg:block fixed z-50"
                  style={{
                    top: `${popoverPosition.top}px`,
                    left: `${popoverPosition.left}px`,
                  }}
                  onMouseEnter={() => setHoveredAgencyUsers(true)}
                  onMouseLeave={() => setHoveredAgencyUsers(false)}
                >
                  {/* Invisible bridge to fill the gap */}
                  <div
                    className="absolute -left-8 top-0 w-8 h-full"
                    onMouseEnter={() => setHoveredAgencyUsers(true)}
                  />
                  {/* Popover content */}
                  <div className="ml-8 min-w-[280px] max-w-[320px] rounded-lg bg-[#1C1C1E] p-4 shadow-lg border border-[#3A3A3C] max-h-[400px] overflow-y-auto">
                    <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[#3A3A3C]/50">
                      <Users className="h-5 w-5 text-[#F33363]" />
                      <h3 className="text-base font-semibold text-white">Agency Users</h3>
                    </div>
                    <div className="space-y-3">
                      {agencyUsers.map((agencyUser) => (
                        <div
                          key={agencyUser.id}
                          className="group p-3 rounded-lg hover:bg-[#2C2C2E] transition-colors border border-[#3A3A3C]/30"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start gap-2">
                                <div className="w-8 h-8 rounded-full bg-[#F33363]/20 flex items-center justify-center flex-shrink-0">
                                  <UserCircle className="h-4 w-4 text-[#F33363]" />
                                </div>
                                <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                                  <p className="text-sm font-semibold truncate text-white">
                                    {agencyUser.name}
                                  </p>
                                  <div className="flex flex-col gap-1">
                                    <span className="text-xs text-[#8E8E93]">Access:</span>
                                    <div className="flex items-center gap-2">
                                      {agencyUser.permissions.view && (
                                        <span className="text-xs text-[#8E8E93] flex items-center gap-1">
                                          <Eye className="h-3 w-3" />
                                          view
                                        </span>
                                      )}
                                      {agencyUser.permissions.edit && (
                                        <span className="text-xs text-[#8E8E93] flex items-center gap-1">
                                          <Pencil className="h-3 w-3" />
                                          edit
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={() => {
                                  handleModifyUser(agencyUser.id);
                                  setHoveredAgencyUsers(false);
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded-lg bg-[#2C2C2E] hover:bg-[#3A3A3C] text-white"
                                aria-label="Edit user"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => {
                                  handleDeleteUser(agencyUser.id);
                                  setHoveredAgencyUsers(false);
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400"
                                aria-label="Delete user"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Add User Button */}
                    <div className="mt-4 pt-4 border-t border-[#3A3A3C]/50">
                      <button
                        onClick={() => {
                          handleAddUser();
                          setHoveredAgencyUsers(false);
                        }}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#F33363] hover:bg-[#E02E57] text-white text-sm font-medium transition-colors"
                      >
                        <UserPlus className="h-4 w-4" />
                        <span>Add User</span>
                      </button>
                    </div>
                  </div>
                </div>,
                document.body
              )
            ) : null}
          </aside>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Page Content */}
          <main data-ptr-root className="flex-1 overflow-y-auto bg-[#0B0B0D] p-4 lg:p-6">
            <div className="w-full ">
              {/* Hero / Context Header */}
              <section className="mt-1 mb-2">
                <div className="space-y-3">
                  <div className="inline-flex items-center gap-2 rounded-full bg-[#1C1C1E] border border-[#3A3A3C] px-3 py-1 w-fit">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F33363]" />
                    <span className="text-[20px] font-black tracking-[0.2em] text-[#8E8E93] uppercase">
                      Your Artists
                    </span>
                  </div>

                </div>
              </section>

              {/* Your Artists Section */}
              <section className=" rounded-3xl ">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4 lg:mb-6">
                  <div className="space-y-1">
                    {/* <h2 className="text-lg lg:text-xl font-semibold text-white">Your Artists</h2> */}
                    <p className="text-xs lg:text-sm text-[#8E8E93]">
                      {artists.length === 0
                        ? 'No artists yet. Start by adding your first artist.'
                        : 'Tap an artist to switch into their view. Use options to remove if needed.'}
                    </p>
                  </div>
                  <div className="flex justify-between items-center gap-3">
                    {/* View toggle - iOS style segmented control */}
                    <div className="inline-flex items-center rounded-full bg-[#0B0B0D] p-1 border border-[#2C2C2E]">
                      <button
                        type="button"
                        onClick={() => setViewMode('grid')}
                        className={cn(
                          'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                          viewMode === 'grid'
                            ? 'bg-white text-black'
                            : 'text-[#8E8E93] hover:text-white'
                        )}
                        aria-label="Grid view"
                      >
                        <Grid3x3 className="h-3.5 w-3.5" />

                      </button>
                      <button
                        type="button"
                        onClick={() => setViewMode('list')}
                        className={cn(
                          'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                          viewMode === 'list'
                            ? 'bg-white text-black'
                            : 'text-[#8E8E93] hover:text-white'
                        )}
                        aria-label="List view"
                      >
                        <List className="h-3.5 w-3.5" />

                      </button>
                    </div>
                    {!isSubUser && (
                      <button
                        type="button"
                        onClick={handleAddArtist}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add Artist
                      </button>
                    )}
                  </div>
                </div>

                {/* Empty state */}
                {artists.length === 0 && !isSubUser && (
                  <div className="rounded-2xl border border-dashed border-[#3A3A3C] bg-[#0B0B0D] px-4 py-6 flex flex-col items-start gap-3">
                    <p className="text-sm text-white font-medium">Create your first artist</p>
                    <p className="text-xs text-[#8E8E93]">
                      We’ll guide you through adding category, genres and event types so your roster is ready to book.
                    </p>
                    {!isSubUser && (
                      <button
                        type="button"
                        onClick={handleAddArtist}
                        className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add artist
                      </button>
                    )}
                  </div>
                )}

                {artists.length > 0 && (
                  <>
                    {/* Grid view */}
                    {viewMode === 'grid' && (
                      <div className="mt-3 grid grid-cols-2 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3 lg:gap-4 mb-8 md:mb-12">
                        {artists.map((artist) => (
                          <button
                            key={artist.id}
                            type="button"
                            onClick={async () => {
                              if (deleteArtistJustClickedRef.current) {
                                deleteArtistJustClickedRef.current = false;
                                return;
                              }
                              setIsSwitchingArtist(true);
                              try {
                                clearArtistSessionCaches();
                                await AuthService.setActiveArtist(artist.id);
                                await fetchArtistDetails();
                                navigate('/user-home');
                              } catch (err) {
                                toast.error('We couldn’t open this artist. Please try again.');
                              } finally {
                                setIsSwitchingArtist(false);
                              }
                            }}
                            className="relative flex flex-col items-center justify-between rounded-2xl bg-[#252527]/50 px-3 py-4 text-left transition-colors hover:bg-[#3A3A3C]"
                          >
                            {!isSubUser && (
                              <DropdownMenu
                                trigger={
                                  <button
                                    type="button"
                                    className="absolute top-2 right-2 rounded-full p-1.5 hover:bg-[#2C2C2E] transition-colors"
                                    aria-label="Artist options"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <MoreVertical className="h-3.5 w-3.5 text-[#8E8E93]" />
                                  </button>
                                }
                                align="end"
                              >
                                <DropdownMenuItem
                                  onClick={() => {
                                    deleteArtistJustClickedRef.current = true;
                                    handleRemoveArtist(artist);
                                  }}
                                  destructive
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete artist
                                </DropdownMenuItem>
                              </DropdownMenu>
                            )}
                            <div className="flex flex-1 flex-col items-center justify-center gap-2 pt-2 pb-1">
                              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#F33363] overflow-hidden">
                                {artist.avatar ? (
                                  <img
                                    src={artist.avatar}
                                    alt={artist.name}
                                    className="h-full w-full rounded-full object-cover"
                                  />
                                ) : (
                                  <UserCircle className="h-7 w-7 text-white" />
                                )}
                              </div>
                              <p className="line-clamp-2 text-center text-xs font-medium text-white">
                                {artist.name}
                              </p>
                            </div>
                            <div className="mt-1 w-full rounded-full bg-[#1C1C1E] px-2 py-1 text-[10px] font-semibold text-[#8E8E93] flex items-center justify-center gap-1">
                              <span>Go to Dashboard</span>
                              <ChevronsRight className="h-3 w-3 text-[#F33363]" />
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* List view */}
                    {viewMode === 'list' && (
                      <div className="mt-3 space-y-3 mb-8 md:mb-12">
                        {artists.map((artist) => (
                          <div
                            key={artist.id}
                            className="flex items-center justify-between rounded-2xl bg-[#252527]/50 px-3 py-3 gap-3"
                          >
                            <button
                              type="button"
                              className="flex flex-1 items-center gap-3 text-left"
                              onClick={async () => {
                                if (deleteArtistJustClickedRef.current) {
                                  deleteArtistJustClickedRef.current = false;
                                  return;
                                }
                                setIsSwitchingArtist(true);
                                try {
                                  clearArtistSessionCaches();
                                  await AuthService.setActiveArtist(artist.id);
                                  await fetchArtistDetails();
                                  navigate('/user-home');
                                } catch (err) {
                                  toast.error('We couldn’t open this artist. Please try again.');
                                } finally {
                                  setIsSwitchingArtist(false);
                                }
                              }}
                            >
                              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F33363] overflow-hidden flex-shrink-0">
                                {artist.avatar ? (
                                  <img
                                    src={artist.avatar}
                                    alt={artist.name}
                                    className="h-full w-full rounded-full object-cover"
                                  />
                                ) : (
                                  <UserCircle className="h-5 w-5 text-white" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-white">{artist.name}</p>
                                <p className="text-[11px] text-[#8E8E93]">Tap to open dashboard</p>
                              </div>
                            </button>
                            {!isSubUser && (
                              <DropdownMenu
                                trigger={
                                  <button
                                    type="button"
                                    className="rounded-full p-2 hover:bg-[#2C2C2E] transition-colors flex-shrink-0"
                                    aria-label="Artist options"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <MoreVertical className="h-4 w-4 text-[#8E8E93]" />
                                  </button>
                                }
                                align="end"
                              >
                                <DropdownMenuItem
                                  onClick={() => {
                                    deleteArtistJustClickedRef.current = true;
                                    handleRemoveArtist(artist);
                                  }}
                                  destructive
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete artist
                                </DropdownMenuItem>
                              </DropdownMenu>
                            )}
                          </div>
                        ))}

                        {/* {!isSubUser && (
                          <button
                            type="button"
                            onClick={handleAddArtist}
                            className="flex w-full items-center justify-center py-3"
                          >
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap">
                              <Plus className="h-3.5 w-3.5" />
                              Add new artist
                            </span>
                          </button>
                        )} */}
                      </div>
                    )}
                  </>
                )}
              </section>

              {/* Team / Agency Users Section - hidden for sub users */}
              {!isSubUser && (
                <section className=" rounded-3xl ">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4 lg:mb-5">
                    <div className="space-y-1">
                      <section className="mt-1 mb-2">
                        <div className="space-y-3">
                          <div className="inline-flex items-center gap-2 rounded-full bg-[#1C1C1E] border border-[#3A3A3C] px-3 py-1 w-fit">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#F33363]" />
                            <span className="text-[20px] font-black tracking-[0.2em] text-[#8E8E93] uppercase">
                              Your Team
                            </span>
                          </div>

                        </div>
                      </section>
                      <p className="text-xs lg:text-sm text-[#8E8E93]">
                        Control who can view or edit your artists. Each user sees only the artists assigned to them.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddUser}
                      className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap"
                    >
                      <UserPlus className="h-3.5 w-3.5" />
                      Add User
                    </button>
                  </div>

                  {agencyUsers.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-[#3A3A3C] bg-[#0B0B0D] px-4 py-5 text-sm text-[#8E8E93]">
                      You don’t have any agency users yet. Add your team members so they can help manage artists and
                      bookings.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 pb-16">
                      {agencyUsers.map((agencyUser) => (
                        <div
                          key={agencyUser.id}
                          className="flex flex-row gap-3 rounded-2xl bg-[#252527]/50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="flex items-start gap-3 min-w-0">
                            <div className="mt-1 flex h-9 w-9 items-center justify-center rounded-full bg-[#F33363]/15 flex-shrink-0">
                              <UserCircle className="h-5 w-5 text-[#F33363]" />
                            </div>
                            <div className="min-w-0 space-y-1">
                              <p className="truncate text-sm font-semibold text-white">
                                {agencyUser.name}
                              </p>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#8E8E93]">
                                {agencyUser.email && <span className="truncate">{agencyUser.email}</span>}
                                {agencyUser.phoneNumber && (
                                  <span className="truncate">· {agencyUser.phoneNumber}</span>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-2 mt-1">
                                {agencyUser.permissions.view && (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-[#2C2C2E] px-2 py-1 text-[10px] font-semibold text-[#8E8E93]">
                                    <Eye className="h-3 w-3" />
                                    Can view
                                  </span>
                                )}
                                {agencyUser.permissions.edit && (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-[#2C2C2E] px-2 py-1 text-[10px] font-semibold text-[#8E8E93]">
                                    <Pencil className="h-3 w-3" />
                                    Can edit
                                  </span>
                                )}
                                {agencyUser.assignedArtists && agencyUser.assignedArtists.length > 0 && (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-[#0B0B0D] px-2 py-1 text-[10px] font-semibold text-[#8E8E93]">
                                    <Users className="h-3 w-3" />
                                    {agencyUser.assignedArtists.length} artist
                                    {agencyUser.assignedArtists.length > 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleModifyUser(agencyUser.id)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#2C2C2E] text-[#8E8E93] hover:bg-[#3A3A3C] hover:text-white transition-colors"
                              aria-label="Edit user"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Add Artist Sheet */}
      <Sheet
        open={isAddArtistOpen}
        onOpenChange={setIsAddArtistOpen}
        title="Add New Artist"
        description="Add a new artist to your agency with specific category, genres, and event types."
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmitArtist();
          }}
          className="space-y-6 w-full"
        >
          {/* Professional Name */}
          <div className="w-full">
            <Input
              label="Professional Name *"
              placeholder="Enter professional name"
              value={professionalName}
              onChange={(e) => {
                setProfessionalName(e.target.value);
                if (errors.professionalName) {
                  setErrors((prev) => ({ ...prev, professionalName: '' }));
                }
              }}
              error={errors.professionalName}
              hideErrorText
            />
          </div>

          {/* Category */}
          <div className="w-full">
            <Select
              label="Category *"
              placeholder="Select category"
              options={categoryOptions}
              value={category}
              onChange={(value) => {
                setCategory(value);
                setGenres([]);
                setGenreOptions([]);
                // Fetch subcategories (genres) for the selected category using its slug
                const selected = categories.find(
                  (c) => c.slug === value
                );
                if (selected) {
                  setIsLoadingGenres(true);
                  AuthService.getSubcategories(selected.slug)
                    .then((subs) => {
                      setGenreOptions(
                        (subs ?? []).map((s) => ({
                          value: String(s.id),
                          label: s.name,
                        }))
                      );
                    })
                    .catch(() => {
                      toast.error('We couldn’t load genres. Please try again.');
                    })
                    .finally(() => {
                      setIsLoadingGenres(false);
                    });
                }
                if (errors.category) {
                  setErrors((prev) => ({ ...prev, category: '' }));
                }
              }}
              error={errors.category}
              hideErrorText
            />
          </div>

          {/* Genres */}
          <div className="w-full">
            <MultiSelect
              label="Genres *"
              options={genreOptions}
              value={genres}
              onChange={(value) => {
                const next =
                  isInfluencerCategorySelected && value.length > 0
                    ? [value[value.length - 1]]
                    : value;
                setGenres(next);
                if (errors.genres) {
                  setErrors((prev) => ({ ...prev, genres: '' }));
                }
              }}
              error={errors.genres}
              hideErrorText
            />
            {isLoadingGenres && (
              <Loader size="small" />
            )}
          </div>

          {/* Event Types */}
          <div className="w-full">
            <MultiSelect
              label="Event Types *"
              options={eventTypeOptions}
              value={eventTypes}
              onChange={(value) => {
                setEventTypes(value);
                if (errors.eventTypes) {
                  setErrors((prev) => ({ ...prev, eventTypes: '' }));
                }
              }}
              error={errors.eventTypes}
              hideErrorText
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4">
            <Button
              type="submit"
              variant="active"
              className="px-8"
              isLoading={isSubmittingArtist}
              disabled={isSubmittingArtist}
            >
              Add Artist
            </Button>
          </div>
        </form>
      </Sheet>

      {/* Remove Artist Sheet */}
      <Sheet
        open={isRemoveArtistOpen}
        onOpenChange={setIsRemoveArtistOpen}
        title="Remove Artist"
        description="Please fill the form below to remove this artist from your agency."
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmitRemoveArtist();
          }}
          className="space-y-6 w-full"
        >
          {/* Consolidated Warning */}
          <div className="bg-red-500/10 border border-red-500/30 rounded-[0.75rem] p-4">
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <p className="text-sm font-semibold text-red-400">Critical Warning</p>
              </div>
              <p className="text-sm text-red-300/90">
                You are about to permanently remove <span className="font-semibold">'{selectedArtist?.name}'</span> from your agency.
                This action is irreversible and will permanently delete all associated data, bookings, and history.
              </p>
            </div>
          </div>

          {/* Reason for Removal */}
          <div className="w-full">
            <label className="mb-2 flex items-center gap-2 text-sm font-medium text-white">
              <MessageSquare className="h-4 w-4" />
              Reason for Removal *
            </label>
            <textarea
              value={deleteArtistForm.reason}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 200) {
                  setDeleteArtistForm((prev) => ({ ...prev, reason: value }));
                  validateDeleteArtistField('reason', value);
                }
              }}
              placeholder="Please provide a detailed reason for removing this artist..."
              rows={4}
              className={cn(
                'flex w-full rounded-[0.75rem] bg-[#252527] px-4 py-3 text-sm text-white placeholder:text-[#8E8E93]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D]',
                'resize-none',
                removeErrors.reason && 'ring-2 ring-destructive'
              )}
            />
            <div className="flex items-center justify-end mt-1">
              <p className={cn(
                'text-xs ml-auto',
                deleteArtistForm.reason.length === 200 ? 'text-red-400' : 'text-[#8E8E93]'
              )}>
                {deleteArtistForm.reason.length}/200 characters
              </p>
            </div>
          </div>

          {/* Profile Managed By */}
          <div className="w-full">
            <div className="mb-2 flex items-center gap-2">
              <UserCircle className="h-4 w-4 text-white" />
              <label className="text-sm font-medium text-white">Profile Managed By *</label>
            </div>
            <Select
              placeholder="Select who will manage the artist"
              options={profileManagedByOptions}
              value={deleteArtistForm.managedBy}
              onChange={(value) => {
                setDeleteArtistForm((prev) => ({
                  ...prev,
                  managedBy: value,
                  agency_name: '',
                  full_name: '',
                  email: '',
                  contact_no: '',
                  relation_to_artist: '',
                }));
                if (removeErrors.managedBy) {
                  setRemoveErrors((prev) => ({ ...prev, managedBy: '' }));
                }
              }}
              error={removeErrors.managedBy}
              hideErrorText
            />
          </div>

          {/* Dynamic fields based on Profile Managed By */}
          <AnimatePresence mode="wait">
            {deleteArtistForm.managedBy && (
              <motion.div
                key={deleteArtistForm.managedBy}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
                className="space-y-4 overflow-hidden"
              >
                {renderDeleteArtistDynamicFields()}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="active"
              onClick={handleCloseRemoveSheet}
              disabled={isDeletingArtist}
              className="bg-[#252527] hover:bg-[#3A3A3C] text-white"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="active"
              disabled={isDeletingArtist}
              className="bg-red-600 hover:bg-red-700 active:bg-red-800 px-6"
            >
              <Trash2 className="h-4 w-4" />
              {isDeletingArtist ? 'Removing...' : 'Remove Artist'}
            </Button>
          </div>
        </form>
      </Sheet>

      {/* Add User Sheet */}
      <Sheet
        open={isAddUserOpen}
        onOpenChange={setIsAddUserOpen}
        title="Add New User"
        description="Add a new user to your agency with specific permissions and artist access."
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmitUser();
          }}
          className="space-y-6 w-full"
        >
          {/* Full Name */}
          <div className="w-full">
            <Input
              label="Full Name"
              placeholder="Enter full name"
              value={fullName}
              onChange={(e) => {
                setFullName(e.target.value);
                if (userErrors.fullName) {
                  setUserErrors((prev) => ({ ...prev, fullName: '' }));
                }
              }}
              error={userErrors.fullName}
              hideErrorText
            />
          </div>

          {/* Phone Number */}
          <div className="w-full">
            <PhoneInput
              label="Phone Number"
              placeholder="Enter 10-digit phone number"
              value={phoneNumber}
              onChange={(value) => {
                setPhoneNumber(value);
                if (userErrors.phoneNumber) {
                  setUserErrors((prev) => ({ ...prev, phoneNumber: '' }));
                }
              }}
              error={userErrors.phoneNumber}
              hideErrorText
            />
          </div>

          {/* Email Address */}
          <div className="w-full">
            <Input
              label="Email Address"
              type="email"
              placeholder="Enter email address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (userErrors.email) {
                  setUserErrors((prev) => ({ ...prev, email: '' }));
                }
              }}
              error={userErrors.email}
              hideErrorText
            />
          </div>

          {/* Password */}
          <div className="w-full">
            <Input
              label="Password"
              type="password"
              placeholder="Create a password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (userErrors.password) {
                  setUserErrors((prev) => ({ ...prev, password: '' }));
                }
              }}
              error={userErrors.password}
              hideErrorText
              showPasswordToggle
              showPasswordStrength
            />
          </div>

          {/* Assign Artists */}
          <div className="w-full">
            <MultiSelect
              label="Assign Artists"
              options={artistOptions}
              value={assignedArtists}
              onChange={(value) => {
                setAssignedArtists(value);
                if (userErrors.assignedArtists) {
                  setUserErrors((prev) => ({ ...prev, assignedArtists: '' }));
                }
              }}
              error={userErrors.assignedArtists}
              hideErrorText
            />
          </div>

          {/* Permissions */}
          <div className="w-full">
            <label className="mb-3 block text-sm font-medium text-white">Permissions</label>
            <div className="space-y-3">
              <Checkbox
                checked={canView}
                onCheckedChange={() => { }}
                label="Can View"
                disabled
              />
              <Checkbox
                checked={canEdit}
                onCheckedChange={(checked) => setCanEdit(checked === true)}
                label="Can Edit"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-4">
            <Button
              type="submit"
              variant="active"
              className="px-8"
              disabled={isSubmittingUser}
              isLoading={isSubmittingUser}
            >
              Add User
            </Button>
            <Button
              type="button"
              variant="active"
              onClick={handleCloseAddUserSheet}
              className="bg-[#252527] hover:bg-[#3A3A3C] text-white px-8"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Sheet>

      {/* Edit User Sheet */}
      <Sheet
        open={isEditUserOpen}
        onOpenChange={setIsEditUserOpen}
        title="Edit Existing User"
        description="Edit user information and permissions. Only modified fields will be updated."
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmitEditUser();
          }}
          className="space-y-6 w-full"
        >
          {/* Full Name - prefilled */}
          <div className="w-full">
            <Input
              label="Full Name *"
              placeholder="Enter full name"
              value={editFullName}
              onChange={(e) => {
                setEditFullName(e.target.value);
                if (editUserErrors.fullName) {
                  setEditUserErrors((prev) => ({ ...prev, fullName: '' }));
                }
              }}
              error={editUserErrors.fullName}
              hideErrorText
            />
          </div>

          {/* Email - prefilled */}
          <div className="w-full">
            <Input
              label="Email *"
              type="email"
              placeholder="Enter email address"
              value={editEmail}
              onChange={(e) => {
                setEditEmail(e.target.value);
                if (editUserErrors.email) {
                  setEditUserErrors((prev) => ({ ...prev, email: '' }));
                }
              }}
              error={editUserErrors.email}
              hideErrorText
            />
          </div>

          {/* Phone Number - prefilled */}
          <div className="w-full">
            <PhoneInput
              label="Phone Number *"
              placeholder="Enter 10-digit phone number"
              value={editPhoneNumber}
              onChange={(value) => {
                setEditPhoneNumber(value);
                if (editUserErrors.phoneNumber) {
                  setEditUserErrors((prev) => ({ ...prev, phoneNumber: '' }));
                }
              }}
              error={editUserErrors.phoneNumber}
              hideErrorText
            />
          </div>

          {/* New Password - optional, not prefilled */}
          <div className="w-full">
            <Input
              label="New password (optional)"
              type="password"
              placeholder="Leave blank to keep current password"
              value={editPassword}
              onChange={(e) => {
                setEditPassword(e.target.value);
                if (editUserErrors.password) {
                  setEditUserErrors((prev) => ({ ...prev, password: '' }));
                }
              }}
              error={editUserErrors.password}
              hideErrorText
              showPasswordToggle
              showPasswordStrength={!!editPassword}
            />
          </div>

          {/* Permissions - prefilled, modifiable */}
          <div className="w-full">
            <label className="mb-3 block text-sm font-medium text-white">Permissions</label>
            <div className="flex items-center gap-6">
              <Checkbox
                checked={editCanView}
                onCheckedChange={(checked) => setEditCanView(checked === true)}
                label="Can View"
              />
              <Checkbox
                checked={editCanEdit}
                onCheckedChange={(checked) => setEditCanEdit(checked === true)}
                label="Can Edit"
              />
            </div>
          </div>

          {/* Artist Access - prefilled, modifiable */}
          <div className="w-full">
            <label className="mb-3 block text-sm font-medium text-white">Artist Access</label>
            <div className="flex items-center gap-6 flex-wrap">
              {artists.map((artist) => {
                const isSelected = editAssignedArtists.includes(artist.id);
                return (
                  <Checkbox
                    key={artist.id}
                    checked={isSelected}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setEditAssignedArtists([...editAssignedArtists, artist.id]);
                      } else {
                        setEditAssignedArtists(editAssignedArtists.filter((id) => id !== artist.id));
                      }
                    }}
                    label={artist.name}
                  />
                );
              })}
            </div>
          </div>

          {/* Action Buttons - same layout as Remove Artist / Add User sheets */}
          <div className="flex items-center justify-end gap-3 pt-4">
            {selectedUser && (
              <Button
                type="button"
                variant="active"
                onClick={async () => {
                  const ok = await handleDeleteUser(selectedUser.id);
                  if (ok) handleCloseEditUserSheet();
                }}
                disabled={!!isDeletingUserId}
                className="bg-[#252527] hover:bg-[#3A3A3C] text-red-400 hover:text-red-300 border border-[#3A3A3C] px-6"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            )}
            <Button
              type="submit"
              variant="active"
              className="px-8"
              disabled={isSubmittingEditUser}
              isLoading={isSubmittingEditUser}
            >
              Update User
            </Button>
          </div>
        </form>
      </Sheet>

      {/* Mobile Sidebar Drawer - Slides from Left */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1.0] }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
            />
            {/* Drawer */}
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{
                duration: 0.3,
                ease: [0.25, 0.1, 0.25, 1.0],
              }}
              style={{ willChange: 'transform' }}
              className="fixed top-0 left-0 h-full w-72 bg-[#1C1C1E]/60 backdrop-blur-xl border-r border-[#3A3A3C] flex flex-col shadow-2xl z-50 lg:hidden overflow-hidden"
            >
              {/* Profile Section */}
              {/* Profile Section */}
              <div className="relative px-6 pt-16 pb-4">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-20 h-20 rounded-full bg-[#3A3A3C] flex items-center justify-center shadow-[0_0_30px_rgba(0,0,0,0.6)]">
                    <Building2 className="h-10 w-10 text-[#8E8E93]" />
                  </div>
                  <div className="text-center space-y-1">
                    <h2 className="text-lg font-black tracking-tight text-white">{agencyName}</h2>
                    <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#8E8E93]">
                      Agency Console
                    </p>
                  </div>
                </div>
                <div className="mt-6 flex justify-center">
                  <div className="h-px w-full bg-gradient-to-r from-transparent via-[#3A3A3C] to-transparent" />
                </div>
              </div>

              {/* Agency Console Content */}
              <div className="flex flex-col flex-1 min-h-0 w-full px-4 pb-3">
                <div className="flex flex-col gap-3 w-full flex-shrink-0">
                  {/* Snapshot card */}
                  <div className="rounded-2xl bg-[#0B0B0D] border border-[#3A3A3C] px-3 py-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-xl bg-[#F33363]/10 flex items-center justify-center">
                        <Building2 className="h-4 w-4 text-[#F33363]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8E8E93]">
                          Artist Managed
                        </p>
                        <p className="text-sm font-semibold text-white truncate">
                          {artists.length}
                        </p>
                      </div>
                    </div>
                    {/* <div className="mt-2 grid grid-cols-1 gap-2">
                      <div className="rounded-xl bg-[#1C1C1E] px-2 py-2">
                        <p className="text-[10px] uppercase tracking-[0.18em] text-[#8E8E93] mb-1">
                          Artists
                        </p>
                        <p className="text-sm font-semibold text-white">
                          <span className="text-base font-bold text-[#F33363]">{artists.length}</span>
                        </p>
                      </div>
                    </div> */}
                  </div>

                  {/* Agency Users mini list */}

                </div>
              </div>

              {/* Logout Button - match UserHome style */}
              <div className="p-4 mt-auto">
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full px-4 py-2.5 rounded-lg bg-[#2C2C2E] hover:bg-[#3A3A3C] text-white text-sm font-medium transition-colors"
                >
                  Logout
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Notifications Sheet */}
      <Sheet
        open={isNotificationsOpen}
        onOpenChange={setIsNotificationsOpen}
        title="Notifications"
        description="View your recent notifications and updates."
      >
        <div className="w-full flex items-center justify-center min-h-[400px]">
          {/* TODO: Replace with actual notifications from API */}
          <div className="flex flex-col items-center justify-center">
            <Bell className="h-12 w-12 text-[#8E8E93] mb-3 opacity-50" />
            <p className="text-[#8E8E93] text-sm text-center">No notifications</p>
            <p className="text-[#8E8E93] text-xs mt-1 text-center">You're all caught up!</p>
          </div>
        </div>
      </Sheet>
    </div>
  );
};
